<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import ToolboxItemPill from './ToolboxItemPill.vue';

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  emptyState: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const selectedValues = ref([...props.modelValue]);
const open = ref(false);
const search = ref('');
const searchInputRef = ref(null);

const filteredOptions = computed(() => {
  const term = search.value.toLowerCase().trim();
  if (!term) return props.options;
  return props.options.filter(option => {
    const label = optionLabel(option).toLowerCase();
    return label.includes(term);
  });
});

const selectedOptions = computed(() =>
  selectedValues.value
    .map(value =>
      props.options.find(
        opt =>
          optionValue(opt) === value ||
          String(optionValue(opt)) === String(value)
      )
    )
    .filter(Boolean)
);

const selectPlaceholder = computed(
  () => props.placeholder || t('COMBOBOX.PLACEHOLDER')
);

function optionValue(option) {
  return option.value ?? option.id ?? option.key;
}

function optionLabel(option) {
  return (
    option.label || option.title || option.name || String(optionValue(option))
  );
}

function toPillItem(option) {
  return {
    ...option,
    id: optionValue(option),
    title: optionLabel(option),
    itemType: option.itemType || 'attribute',
    isMainTool: true,
  };
}

function isSelected(option) {
  const value = optionValue(option);
  return selectedValues.value.some(
    selected => selected === value || String(selected) === String(value)
  );
}

function isLocked(option) {
  return Boolean(option?.locked);
}

function toggleOption(option) {
  if (isLocked(option) && isSelected(option)) return;

  const value = optionValue(option);
  const index = selectedValues.value.findIndex(
    selected => selected === value || String(selected) === String(value)
  );
  if (index === -1) {
    selectedValues.value = [...selectedValues.value, value];
  } else {
    selectedValues.value = selectedValues.value.filter((_, i) => i !== index);
  }
  emit('update:modelValue', selectedValues.value);
}

function removeValue(value) {
  const option = props.options.find(
    opt =>
      optionValue(opt) === value || String(optionValue(opt)) === String(value)
  );
  if (isLocked(option)) return;

  selectedValues.value = selectedValues.value.filter(
    selected => selected !== value && String(selected) !== String(value)
  );
  emit('update:modelValue', selectedValues.value);
}

function clearAll() {
  selectedValues.value = selectedValues.value.filter(value => {
    const option = props.options.find(
      opt =>
        optionValue(opt) === value || String(optionValue(opt)) === String(value)
    );
    return isLocked(option);
  });
  emit('update:modelValue', selectedValues.value);
}

const hasClearableSelection = computed(() =>
  selectedOptions.value.some(option => !isLocked(option))
);

function toggleDropdown() {
  if (props.disabled) return;
  open.value = !open.value;
  if (open.value) {
    search.value = '';
    nextTick(() => searchInputRef.value?.focus());
  }
}

watch(
  () => props.modelValue,
  newValue => {
    selectedValues.value = [...newValue];
  }
);
</script>

<template>
  <div
    class="relative w-full min-w-0"
    :class="{ 'opacity-50 pointer-events-none': disabled }"
  >
    <OnClickOutside @trigger="open = false">
      <div
        class="flex flex-wrap w-full gap-2 px-3 py-2.5 border rounded-lg cursor-pointer bg-n-alpha-black2 min-h-[42px] transition-all"
        :class="{
          'border-n-weak dark:border-n-weak hover:border-n-slate-6': !open,
          'border-woot-700': open,
        }"
        @click="toggleDropdown"
      >
        <div
          v-for="option in selectedOptions"
          :key="optionValue(option)"
          class="w-fit max-w-full"
          @click.stop
        >
          <ToolboxItemPill :item="toPillItem(option)">
            <template v-if="!isLocked(option)" #trailing>
              <button
                type="button"
                class="flex flex-shrink-0 ml-auto hover:opacity-80"
                :class="
                  option.itemType === 'library'
                    ? 'text-blue-800'
                    : 'text-amber-800'
                "
                @click="removeValue(optionValue(option))"
              >
                <i class="i-lucide-x w-3 h-3 opacity-70" />
              </button>
            </template>
          </ToolboxItemPill>
        </div>
        <span
          v-if="selectedOptions.length === 0"
          class="flex items-center text-sm text-n-slate-11"
        >
          {{ selectPlaceholder }}
        </span>
      </div>

      <div
        v-if="open"
        class="absolute z-50 mt-1 w-full rounded-lg border border-n-weak dark:border-n-strong/50 bg-white dark:bg-n-slate-2 shadow-lg overflow-hidden"
      >
        <div class="p-2 border-b border-n-slate-3 dark:border-n-slate-7">
          <input
            ref="searchInputRef"
            v-model="search"
            type="text"
            class="w-full px-3 py-2 text-sm rounded-md border border-n-slate-4 dark:border-n-slate-7 bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-9"
            :placeholder="
              searchPlaceholder ||
              $t('ASSISTANT_MGMT.TOOLBOX.SEARCH_PLACEHOLDER')
            "
            @click.stop
          />
          <button
            v-if="hasClearableSelection"
            type="button"
            class="mt-1.5 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 transition-colors"
            @click.stop="clearAll"
          >
            {{ $t('ASSISTANT_MGMT.PROCEDURES.STANDARD.WIZARD.CLEAR_ALL') }}
          </button>
        </div>
        <div class="max-h-64 overflow-y-auto p-2 flex flex-col gap-1">
          <button
            v-for="option in filteredOptions"
            :key="optionValue(option)"
            type="button"
            class="flex items-center justify-between gap-2 w-full text-left px-1 py-1 rounded-lg transition-colors"
            :class="
              isLocked(option)
                ? 'cursor-default opacity-90'
                : 'hover:bg-n-slate-2 dark:hover:bg-n-slate-3'
            "
            @click.stop="toggleOption(option)"
          >
            <ToolboxItemPill :item="toPillItem(option)" />
            <i
              v-if="isSelected(option)"
              class="i-lucide-check w-4 h-4 flex-shrink-0 text-woot-700"
            />
          </button>
          <p
            v-if="filteredOptions.length === 0"
            class="px-2 py-3 text-sm text-n-slate-9 text-center"
          >
            {{ emptyState || $t('ASSISTANT_MGMT.TOOLBOX.NO_ITEMS_FOUND') }}
          </p>
        </div>
      </div>
    </OnClickOutside>
  </div>
</template>
